import {
  getErrorMessage,
  useApiClient,
} from "@/lib/api/api.client";
import { useIsEntitled } from "@/lib/auth/permissions/permissions";
import i18n from "@/lib/i18n/i18n";
import { useAuth } from "@frontegg/react";
import { ApiError } from "@soulbyte/core";
import type { ApiErrorResponse, components, Permission } from "@soulbyte/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// =============================================================================
// Types
// =============================================================================

export type PatchTenantRequestPayload =
  components["schemas"]["PatchTenantRequestDTO"];
export type Tenant = components["schemas"]["TenantDTO"];

/**
 * Flattened tenant fields used by the tenant setup wizard.
 */
export type TenantWizardViewModel = {
  industry?: string;
  logo_url?: string;
  plan_key?: string;
  brand_color?: string;
  description?: string;
  company_name?: string;
  sidebar_color?: string;
  company_onboarding?: string;
};

/**
 * Dispatched on the window when a tenant mutation fails with a permission-style
 * `403` so UI (e.g. tenant setup dialog) can close. Listeners are optional.
 */
export const TENANT_MUTATION_FORBIDDEN_EVENT = "soulbyte:tenant_mutation_forbidden";

// =============================================================================
// API tenant → wizard state
// =============================================================================

export function isTenantOnboardingDone(
  company_onboarding: string | undefined,
): boolean {
  return company_onboarding === "done";
}

export function mapTenantToWizardViewModel(
  tenant: Tenant | undefined,
): TenantWizardViewModel | undefined {
  if (!tenant) {
    return undefined;
  }
  return {
    industry: tenant.industry,
    logo_url: tenant.brand?.logo_url,
    plan_key: tenant.plan_key,
    brand_color: tenant.brand?.brand_color,
    description: tenant.description,
    company_name: tenant.company_name,
    sidebar_color: tenant.brand?.sidebar_color,
    company_onboarding: tenant.company_onboarding,
  };
}

// =============================================================================
// PATCH body builders (shared/non-step-specific)
// =============================================================================

export function patchTenantBrandColors(args: {
  brand_color?: string;
  sidebar_color?: string;
}): PatchTenantRequestPayload {
  return {
    brand: {
      brand_color: args.brand_color,
      sidebar_color: args.sidebar_color,
    },
  };
}

export function patchTenantMarkOnboardingDone(): PatchTenantRequestPayload {
  return { company_onboarding: "done" };
}

/** Final branding step: persist colors and mark onboarding complete (Enterprise). */
export function patchTenantAfterBrandingStepComplete(args: {
  brand_color: string;
  sidebar_color: string;
}): PatchTenantRequestPayload {
  return {
    brand: {
      brand_color: args.brand_color,
      sidebar_color: args.sidebar_color,
    },
    company_onboarding: "done",
  };
}

// =============================================================================
// Error handling (403 / permission loss)
// =============================================================================

function readTenantMutationErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object" || !("error" in error)) {
    return undefined;
  }
  const envelope = error as ApiErrorResponse;
  return envelope.error?.code;
}

function readTenantMutationRequiredPermission(error: unknown): string | undefined {
  if (!error || typeof error !== "object" || !("error" in error)) {
    return undefined;
  }
  const envelope = error as ApiErrorResponse & {
    error?: {
      details?: {
        required_permission?: string;
      };
    };
  };
  return envelope.error?.details?.required_permission;
}

function isHttp403(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

function getTenantErrorMessageKey(error: unknown): string {
  const code = readTenantMutationErrorCode(error);
  if (code === "BRAND_NOT_AVAILABLE") {
    return "tenant_error_brand_not_available";
  }
  if (code === "INVALID_ONBOARDING_STEP") {
    return "tenant_error_invalid_onboarding_step";
  }
  if (code === "TENANT_NOT_PROVISIONED") {
    return "tenant_error_not_provisioned";
  }
  if (code === "FILE_TOO_LARGE") {
    return "tenant_logo_error_file_too_large";
  }
  if (code === "INVALID_FILE_TYPE") {
    return "tenant_logo_error_invalid_file_type";
  }
  return "company_setup_error";
}

export function getTenantErrorMessage(error: unknown): string {
  const code = readTenantMutationErrorCode(error);
  if (code === "BRAND_NOT_AVAILABLE") {
    return getErrorMessage(error);
  }
  return i18n.t(getTenantErrorMessageKey(error));
}

async function handleTenantMutationForbiddenSideEffects(args: {
  error: unknown;
  invalidateTenant: () => Promise<unknown>;
}): Promise<boolean> {
  const { error, invalidateTenant } = args;
  const code = readTenantMutationErrorCode(error);

  if (code === "BRAND_NOT_AVAILABLE") {
    toast.error(getTenantErrorMessage(error));
    await invalidateTenant();
    return true;
  }

  if (code === "INVALID_ONBOARDING_STEP") {
    toast.error(getTenantErrorMessage(error));
    await invalidateTenant();
    return true;
  }

  const permissionLike =
    code === "FORBIDDEN" ||
    Boolean(readTenantMutationRequiredPermission(error)) ||
    (isHttp403(error) && code !== "BRAND_NOT_AVAILABLE");

  if (!permissionLike) {
    return false;
  }

  toast.error(i18n.t("tenant_mutation_forbidden"));
  await invalidateTenant();

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TENANT_MUTATION_FORBIDDEN_EVENT));
  }
  return true;
}

// =============================================================================
// Tenant setup wizard
// =============================================================================

export function useTenantSetupWizardEligibility() {
  const canReadTenant = useIsEntitled("organization.branding.read");
  const canModifyTenant = useIsEntitled("organization.branding.modify");
  const canUploadTenantLogo = useIsEntitled("organization.branding.create");

  const canDeleteTenantLogo = canModifyTenant;
  const canOpenWizard = canReadTenant && canModifyTenant;

  return {
    canDeleteTenantLogo,
    canModifyTenant,
    canOpenWizard,
    canReadTenant,
    canUploadTenantLogo,
  };
}

// =============================================================================
// /tenant
// =============================================================================

export function useTenant() {
  const apiClient = useApiClient();
  const isEntitled = useIsEntitled("organization.branding.read");

  const query = apiClient.useQuery("get", "/tenant", {}, {
    enabled: isEntitled,
  });

  const tenant = query.data?.data;
  const wizardViewModel = mapTenantToWizardViewModel(tenant);

  return Object.assign({}, query, {
    errorCode: readTenantMutationErrorCode(query.error),
    isEntitled,
    permissions: [
      "organization.branding.read",
    ] satisfies Permission[],
    tenant,
    wizardViewModel,
  });
}

export function useInvalidateTenant() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return function invalidateTenant() {
    const queryOptions = apiClient.queryOptions("get", "/tenant", {});
    return queryClient.invalidateQueries(queryOptions);
  };
}

// =============================================================================
// Update tenant
// =============================================================================

export function useUpdateTenant() {
  const apiClient = useApiClient();
  const canModifyTenant = useIsEntitled("organization.branding.modify");
  const mutation = apiClient.useMutation("patch", "/tenant");
  const invalidateTenant = useInvalidateTenant();

  return {
    canModifyTenant,
    isPending: mutation.isPending,
    permissions: [
      "organization.branding.modify",
    ] satisfies Permission[],
    updateTenant: async (body: PatchTenantRequestPayload) => {
      try {
        const data = await mutation.mutateAsync({ body });
        await invalidateTenant();
        return data;
      } catch (error) {
        await handleTenantMutationForbiddenSideEffects({
          error,
          invalidateTenant,
        });
        throw error;
      }
    },
  };
}

// =============================================================================
// /tenant/logo
// =============================================================================

async function fetchTenantLogoBlob(args: {
  logoUrl: string;
  accessToken?: string | null;
}): Promise<Blob | null> {
  const trimmed = args.logoUrl.trim();
  if (!trimmed) {
    return Promise.resolve(null);
  }

  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    return Promise.reject(new Error("VITE_API_URL is not set"));
  }

  const resolvedUrl = trimmed.startsWith("http")
    ? trimmed
    : `${apiUrl}${trimmed}`;

  const response = await fetch(resolvedUrl, {
    headers: args.accessToken
      ? { Authorization: `Bearer ${args.accessToken}` }
      : undefined,
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch tenant logo (${response.status})`);
  }

  return response.blob();
}

export function getTenantLogoBlobQueryKey(logoUrl?: string) {
  if (!logoUrl) {
    return ["tenant", "logo"] as const;
  }
  return ["tenant", "logo", logoUrl.trim()] as const;
}

export function useTenantLogoBlob(logoUrl?: string) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: getTenantLogoBlobQueryKey(logoUrl),
    queryFn: () => {
      if (!logoUrl) {
        return Promise.resolve(null);
      }
      return fetchTenantLogoBlob({ logoUrl, accessToken: user?.accessToken });
    },
    enabled: !!logoUrl,
  });

  const tenantLogoBlob = query.data;

  return Object.assign({}, query, {
    tenantLogoBlob,
  });
}

export function useInvalidateTenantLogoBlob() {
  const queryClient = useQueryClient();

  return function invalidateTenantLogoBlob(logoUrl?: string) {
    return queryClient.invalidateQueries({ queryKey: getTenantLogoBlobQueryKey(logoUrl) });
  };
}

// =============================================================================
// Upload tenant logo
// =============================================================================

export function useUploadTenantLogo() {
  const apiClient = useApiClient();

  const mutation = apiClient.useMutation("post", "/tenant/logo");
  const canUploadTenantLogo = useIsEntitled("organization.branding.create");

  const invalidateTenant = useInvalidateTenant();
  const invalidateTenantLogoBlob = useInvalidateTenantLogoBlob();

  async function invalidateTenantAndLogoBlob() {
    await invalidateTenant();
    await invalidateTenantLogoBlob();
    return Promise.resolve();
  }

  return {
    canUploadTenantLogo,
    isPending: mutation.isPending,
    permissions: [
      "organization.branding.create",
    ] satisfies Permission[],
    uploadTenantLogo: async (file: Blob | File) => {
      try {
        const body = new FormData();
        body.append("file", file);
        const data = await mutation.mutateAsync({
          // @ts-expect-error openapi-ts does not infer FormData for multipart endpoints
          body,
        });
        await invalidateTenantAndLogoBlob();
        return data;
      } catch (error) {
        const handled = await handleTenantMutationForbiddenSideEffects({
          error,
          invalidateTenant: invalidateTenantAndLogoBlob,
        });
        if (!handled) {
          toast.error(getTenantErrorMessage(error));
        }
        throw error;
      }
    },
  };
}

// =============================================================================
// Delete tenant logo
// =============================================================================

export function useDeleteTenantLogo() {
  const apiClient = useApiClient();

  const mutation = apiClient.useMutation("delete", "/tenant/logo");
  const canDeleteTenantLogo = useIsEntitled("organization.branding.modify");

  const invalidateTenant = useInvalidateTenant();
  const invalidateTenantLogoBlob = useInvalidateTenantLogoBlob();

  async function invalidateTenantAndLogoBlob() {
    await invalidateTenant();
    await invalidateTenantLogoBlob();
    return Promise.resolve();
  }

  return {
    canDeleteTenantLogo,
    isPending: mutation.isPending,
    permissions: [
      "organization.branding.modify",
    ] satisfies Permission[],
    deleteTenantLogo: async () => {
      try {
        const data = await mutation.mutateAsync({});
        await invalidateTenantAndLogoBlob();
        return data;
      } catch (error) {
        const handled = await handleTenantMutationForbiddenSideEffects({
          error,
          invalidateTenant: invalidateTenantAndLogoBlob,
        });
        if (!handled) {
          toast.error(getTenantErrorMessage(error));
        }
        throw error;
      }
    },
  };
}
